import { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Group, Stack, Text, Title } from '@mantine/core';
import { IconAlertCircle, IconRefresh } from '@tabler/icons-react';
import { APP_VERSION } from '../shared/version';
import { applyPendingUpdate, fetchDeployedVersion } from '../shared/versionCheck';

/**
 * The screen for a URL this build has no route for.
 *
 * WHY IT CHECKS THE VERSION FIRST
 * The app is an installed PWA running in `prompt` mode: a new bundle installs and then WAITS for
 * the user to accept it. So an installed copy keeps serving the routes it was built with, and a
 * link to a route added in a later deploy resolves to nothing — the router matches no route and
 * renders this.
 *
 * That happened on 2026-08-19 with the very first invitation email. The invitee's app was two
 * deploys behind, `/invitations/{token}` did not exist in it, and TanStack Router's default
 * not-found is the bare words "Not Found" on a white page. Nothing suggested the fix was to update
 * the app, and the person seeing it was somebody being invited — the least equipped of anybody to
 * guess that.
 *
 * A deep link is exactly the case where "your copy is old" is the likeliest explanation, so this
 * asks the server what is deployed before it says anything. If a newer build exists, that is the
 * headline and reloading keeps the current URL, which lands the reader back on the link they
 * clicked. Only when the app is already current is a missing page really a missing page.
 */
export function NotFoundRoute() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [deployed, setDeployed] = useState<string | null>(null);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void fetchDeployedVersion().then((version) => {
      if (cancelled) return;
      setDeployed(version);
      setChecked(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const staleBuild = checked && deployed !== null && deployed !== APP_VERSION;

  return (
    <Stack gap="md" p="xl" maw={560} mx="auto">
      {staleBuild ? (
        <>
          <Title order={2}>{t('notFound.staleTitle')}</Title>
          <Alert color="invetrak" variant="light" icon={<IconAlertCircle size={16} />}>
            {t('notFound.staleBody', { deployed, current: APP_VERSION })}
          </Alert>
          <Group>
            {/* Reloads in place, so the URL that produced this survives and the reader lands on
                the page they were trying to reach rather than back at the beginning. */}
            <Button
              color="invetrak"
              leftSection={<IconRefresh size={16} />}
              // Wrapped, not passed straight through: applyPendingUpdate takes an optional
              // version, and a bare handler would hand it the click event as one.
              onClick={() => void applyPendingUpdate()}
            >
              {t('notFound.updateAndRetry')}
            </Button>
          </Group>
        </>
      ) : (
        <>
          <Title order={2}>{t('notFound.title')}</Title>
          <Text size="sm" c="dimmed">
            {checked ? t('notFound.body') : t('shell.loading')}
          </Text>
          <Group>
            <Button color="invetrak" onClick={() => void navigate({ to: '/' })}>
              {t('notFound.goHome')}
            </Button>
          </Group>
        </>
      )}

      <Text size="xs" c="dimmed">
        {t('notFound.version', { version: APP_VERSION })}
      </Text>
    </Stack>
  );
}
